/**
 * Safe reader for `window.app` properties from ESM modules that may
 * load before `app.init()` runs.
 *
 * Custom elements upgrade synchronously during HTML parsing, but
 * `app.properties` is populated on DOMContentLoaded — so a naive
 * `window.app.get(path)` from a connectedCallback throws "Cannot read
 * properties of undefined (reading 'properties')". `appGet()` folds
 * the guard + fallback into a single call so callers don't reinvent
 * the check (or forget it — every Phase 6 ESM file that read from
 * `app.get` had the same latent throw before this helper landed).
 *
 * Returns `fallback` (default: `undefined`) until app is hydrated AND
 * the requested path resolves to a defined value. Doesn't defer or
 * retry — callers that need the live value once it appears should
 * use the `ep:ready` pattern in `log.esm.js`.
 *
 * Source path: theme/js/core/app-get.esm.js
 * Built name : ./app-get.js (siblings imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-04-26
 * Model: Claude Opus 4.7
 *
 * [AppGet]
 */

export function appGet(path, fallback) {
  const a = window.app;
  if (!a || !a.properties || typeof a.get !== 'function') return fallback;
  const value = a.get(path);
  return value === undefined ? fallback : value;
}

/**
 * Has `app.init()` populated `app.properties` yet? Use this in custom-
 * element connectedCallback paths that may run pre-init (HTML parser
 * upgrades elements before DOMContentLoaded) before calling app methods
 * that close over `application.properties` (e.g. `replaceState`,
 * `centsToMoney`, `flags`, `ga`). `safeAppCall` is NOT enough — it only
 * checks the method exists, not that it can run safely.
 */
export function isAppHydrated() {
  return !!(window.app && window.app.properties);
}

/**
 * Safe invoker for `window.app` methods (ga, set, trapFocus, setLastFocused,
 * restoreLastFocused, etc.). No-ops if `window.app` or the named method is
 * missing — folds the repeated `if (window.app && window.app.X) ...` guard
 * pattern into one call.
 *
 * Returns the method's return value, or undefined if the method isn't
 * available.
 */
export function safeAppCall(method, ...args) {
  const a = window.app;
  if (!a || typeof a[method] !== 'function') return undefined;
  return a[method](...args);
}
